import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js/auto';
import { Helmet } from 'react-helmet';

const QuartilesCalculator = () => {
  const [data, setData] = useState('');
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [q3, setQ3] = useState(null);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Quartiles Distribution',
        data: [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
      },
    ],
  });

  const calculateQuartiles = () => {
    const numbers = data.split(',').map(Number).filter(num => !isNaN(num));
    if (numbers.length < 4) {
      alert('Please enter at least 4 numbers to calculate quartiles');
      return;
    }

    const sortedNumbers = [...numbers].sort((a, b) => a - b);
    const n = sortedNumbers.length;

    // Calculate quartiles
    const calculateQuartile = (position) => {
      const index = (n * position) - 1;
      if (index % 1 === 0) {
        return sortedNumbers[index];
      } else {
        const lowerIndex = Math.floor(index);
        const upperIndex = Math.ceil(index);
        return (sortedNumbers[lowerIndex] + sortedNumbers[upperIndex]) / 2;
      }
    };

    const q1Value = calculateQuartile(0.25);
    const q2Value = calculateQuartile(0.5);
    const q3Value = calculateQuartile(0.75);

    setQ1(q1Value);
    setQ2(q2Value);
    setQ3(q3Value);

    // Update chart data
    const chartLabels = Array.from({ length: n }, (_, i) => i + 1);
    setChartData({
      labels: chartLabels,
      datasets: [
        {
          label: 'Quartiles Distribution',
          data: sortedNumbers,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1,
        },
      ],
    });
  };

  return (
    <div className="container mx-auto p-4">
      <Helmet>
        <title>Quartiles Calculator - Calculate Quartiles (Q1, Q2, Q3)</title>
        <meta
          name="description"
          content="Calculate Quartiles (Q1, Q2, Q3) of your data set. Understand data distribution and identify outliers with our Quartiles Calculator."
        />
        <meta
          property="og:title"
          content="Quartiles Calculator - Calculate Quartiles (Q1, Q2, Q3)"
        />
        <meta
          property="og:description"
          content="Calculate Quartiles (Q1, Q2, Q3) of your data set. Understand data distribution and identify outliers with our Quartiles Calculator."
        />
        <meta
          property="twitter:title"
          content="Quartiles Calculator - Calculate Quartiles (Q1, Q2, Q3)"
        />
        <meta
          property="twitter:description"
          content="Calculate Quartiles (Q1, Q2, Q3) of your data set. Understand data distribution and identify outliers with our Quartiles Calculator."
        />
      </Helmet>

      <h1 className="text-3xl font-bold mb-6">Quartiles Calculator</h1>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Calculate Quartiles</h2>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Enter your data (comma-separated numbers):
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={data}
            onChange={(e) => setData(e.target.value)}
          />
        </div>

        <button
          onClick={calculateQuartiles}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Calculate Quartiles
        </button>
      </div>

      {q1 !== null && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Results</h2>
          <div className="space-y-4">
            <div>
              <span className="font-semibold">Q1 (First Quartile):</span> {q1}
            </div>
            <div>
              <span className="font-semibold">Q2 (Second Quartile/Median):</span> {q2}
            </div>
            <div>
              <span className="font-semibold">Q3 (Third Quartile):</span> {q3}
            </div>
          </div>
        </div>
      )}

      {chartData.labels.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Data Distribution</h2>
          <div className="h-[400px]">
            <Line data={chartData} />
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-4">Understanding Quartiles</h2>
        <p className="mb-4">
          Quartiles are values that divide a dataset into four equal parts. They help in understanding the distribution of data and identifying outliers.
        </p>
        <ul className="list-disc list-inside mb-6">
          <li>
            <strong>Q1 (First Quartile):</strong> Represents the 25th percentile - 25% of the data falls below this value.
          </li>
          <li>
            <strong>Q2 (Second Quartile/Median):</strong> Represents the 50th percentile - 50% of the data falls below this value.
          </li>
          <li>
            <strong>Q3 (Third Quartile):</strong> Represents the 75th percentile - 75% of the data falls below this value.
          </li>
        </ul>
        <p className="mb-4">
          Quartiles are particularly useful for:
        </p>
        <ul className="list-disc list-inside">
          <li>Identifying outliers in your data</li>
          <li>Understanding the spread of your data</li>
          <li>Comparing different datasets</li>
          <li>Creating box plots for visual analysis</li>
        </ul>
      </div>

      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
        <ul className="list-disc list-inside mb-6">
          <li>
            <strong>Easy to Use:</strong> Simply enter your comma-separated numbers and get instant results.
          </li>
          <li>
            <strong>Visual Representation:</strong> Interactive line chart shows your data distribution and quartile positions.
          </li>
          <li>
            <strong>Accurate Calculations:</strong> Uses proper statistical methods to calculate quartiles for any dataset.
          </li>
          <li>
            <strong>Outlier Detection:</strong> Helps identify potential outliers in your data based on quartile ranges.
          </li>
          <li>
            <strong>Real-time Results:</strong> Get instant results as soon as you enter your data.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default QuartilesCalculator;
